import React, { useMemo, Suspense, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useTabContext } from 'GlobalState/Context/TabContext';
import { useGlobalContext } from 'GlobalState/Context/GlobalContext';

const isEntityActive = (value) =>
  value === true || value === 1 || value === '1';

// Forms
const EmployeesForm = React.lazy(() =>
  import('components/Form/Manage/ManageEmployeesForm/EmployeesForm')
);

// Table
import {
  useTable,
  useSortBy,
  useFlexLayout,
  useGlobalFilter,
} from 'react-table';
import Label from 'components/Label/Label';
import Edit from 'components/Icons/Edit';
import Button from 'components/Button/Button';
import Crown from 'components/Icons/Crown';
import projectTableGlobalFilter from 'utils/projectsTableGlobalFilter';
import IsActiveCheckbox from 'components/Checkbox/IsActiveCheckbox';

// styles
import cx from 'classnames';
import tableStyles from '../../Tables.module.scss';
import styles from '../Manage.module.scss';

// Data fetching
import { useQuery } from 'react-query';
import instance from 'axiosInstance';
/**
 * The one, the only
 *
 * This is the home page table and it's the most used component of the whole app
 * @see https://react-table.js.org/ - 90% of the stuff is there.
 * @return {JSX} My reports table
 */

// Todo: - Modal on top. Data changes on edit click
const TableEmployeesUI = ({ data }) => {
  // accessor is the "key" in the data
  const {
    state: { searchFilter },
  } = useTabContext();

  const { globalDispatch } = useGlobalContext();

  const globalFilter = useMemo(() => projectTableGlobalFilter, []);

  const columns = useMemo(
    () => [
      {
        Header: '',
        accessor: 'isAdmin',
        maxWidth: 60,
        minWidth: 40,
        width: 40,
        disableSortBy: true,
        disableGlobalFilter: true,
      },
      {
        Header: 'Full Name',
        accessor: 'employee',
        maxWidth: 320,
        minWidth: 220,
        width: 220,
        sortType: (a, b) =>
          a.original.employee.name < b.original.employee.name ? -1 : 1,
      },
      {
        Header: 'Email',
        accessor: 'email',
        maxWidth: 320,
        minWidth: 250,
        width: 250,
      },
      {
        Header: 'Position',
        accessor: 'position',
        maxWidth: 320,
        minWidth: 250,
        width: 250,
      },
      {
        Header: 'Started on',
        accessor: 'contractDay',
        maxWidth: 320,
        minWidth: 160,
        width: 160,
      },
      {
        Header: 'Is Active',
        accessor: 'isActive',
        maxWidth: 320,
        minWidth: 120,
        width: 120,
        disableGlobalFilter: true,
      },
      {
        Header: 'Actions',
        accessor: 'actions',
        maxWidth: 100,
        minWidth: 120,
        width: 120,
        disableSortBy: true,
        disableGlobalFilter: true,
      },
    ],
    []
  );

  const handleEditPopup = (row) => {
    globalDispatch({
      type: 'setModalContent',
      payload: (
        <Suspense fallback={<div>Loading</div>}>
          <EmployeesForm heading="Edit Employee" row={row} type="edit" />
        </Suspense>
      ),
    });
  };

  /**
   * The useTable hook is what gives us the instances to render the table.
   * @see https://react-table.js.org/api/useTable
   */
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    setGlobalFilter,
  } = useTable(
    {
      globalFilter,
      columns,
      data,
      disableSortRemove: true,
    },
    useGlobalFilter,
    useSortBy,
    useFlexLayout
  );

  useEffect(() => {
    setGlobalFilter(searchFilter);
  }, [searchFilter]);

  /**
   * In the header, we are showing the label + can add sorting options
   */

  const tableHeader = useMemo(
    () =>
      headerGroups.map((headerGroup, key) => (
        <div
          key={key}
          className={cx(tableStyles.tr, tableStyles['sticky-row'])}
          {...headerGroup.getHeaderGroupProps()}
        >
          {headerGroup.headers.map((column) => (
            <div
              key={column.id}
              className={`${tableStyles.th} ${styles.th}`}
              {...column.getHeaderProps(column.getSortByToggleProps())}
            >
              <Label
                fontStyle="bold"
                arrow={column.canSort ? 'arrow-down' : ''}
              >
                {column.render('Header')}
              </Label>
            </div>
          ))}
        </div>
      )),
    []
  );

  /**
   * The tableBody is the date we get from the API. Currently static :)
   */
  const tableBody = useMemo(
    () =>
      rows.map((row) => {
        // (Required) Any row that you intend to render in your table needs to be passed to
        // this function before every render.
        prepareRow(row);

        return (
          <div key={row.id} className={tableStyles.tr} {...row.getRowProps()}>
            {row.cells.map((cell) => {
              let output = <Label>{cell.value}</Label>;
              // Standard output

              if (cell.column.id === 'employee') {
                output = (
                  <Link to={`/member/${cell.value.slug}`}>
                    {cell.value.name}
                  </Link>
                );
              }

              if (cell.column.id === 'isAdmin') {
                output = cell.value === true && (
                  <Crown
                    color={'var(--theme-color-primary)'}
                    width="18"
                    height="14"
                  />
                );
              }

              if (cell.column.id === 'isActive') {
                output = (
                  <IsActiveCheckbox
                    endpoint={
                      '/private/admin/employees/active/' + row.original.id
                    }
                    isChecked={isEntityActive(row.original.isActive)}
                    queriesToInvalidate={['employees']}
                  />
                );
              }

              if (cell.column.id === 'actions') {
                output = (
                  <div className={tableStyles['actions-wrapper']}>
                    <Button
                      type="button-primary-inline"
                      icon={<Edit width="20px" height="20px" />}
                      clicked={() => handleEditPopup(row.original)}
                    />
                  </div>
                );
              }

              return (
                <div
                  key={cell.column.id}
                  className={cx(tableStyles.td)}
                  {...cell.getCellProps()}
                >
                  {output}
                </div>
              );
            })}
          </div>
        );
      }),
    [rows]
  );

  return (
    <div className={tableStyles.table} {...getTableProps}>
      <div className={`${tableStyles['thead-sticky']} thead`}>
        {tableHeader}
      </div>
      <div className={tableStyles.tbody} {...getTableBodyProps()}>
        {tableBody}
      </div>
    </div>
  );
};

TableEmployeesUI.propTypes = {
  data: PropTypes.array,
};

const getEmployees = async () => {
  const response = await instance.get('/private/admin/employees');
  return response.data;
};
const TableEmployees = ({ shouldShowInactive }) => {
  const { data } = useQuery('employees', getEmployees);
  const safeEmployees = Array.isArray(data) ? data : [];

  const activeMembers = safeEmployees.filter((member) =>
    isEntityActive(member.isActive)
  );

  return (
    <TableEmployeesUI
      data={shouldShowInactive ? safeEmployees : activeMembers}
    />
  );
};

TableEmployees.propTypes = {
  shouldShowInactive: PropTypes.bool,
};

export default TableEmployees;
